import React from 'react';
import Image from 'frontastic/lib/image';
import Button from 'components/commercetools-ui/atoms/button';
import Link from 'components/commercetools-ui/atoms/link';
import Typography from 'components/commercetools-ui/atoms/typography';
import { Tiempos, NationalSemibold } from 'fonts';
import { GenericSectionContent, Cta } from '../../types';
import { useAdditionalCss } from '../../../helpers/useAdditionalCss';
import { sanitizeHtml, SECTION_SPACING } from '../../../helpers/rendering';

/**
 * Carina — a padded (not full-bleed) image-and-copy row: image one side, title + rich text + CTAs
 * the other, sides swapped by `cards_block_alignment`. Maps the legacy `info-section-with-image` /
 * `section-with-image-and-text` body blocks, e.g. the second and third sections of /au/our-story/
 * and /au/experience-centres/.
 *
 * This is the rendering that Mesa and Solis both used to be. They were duplicates of each other and
 * of this layout, while the names were meant for the category and collection listing headers - so
 * the layout moved here and those two names went back to the headers they describe.
 *
 * A body section, so the title is an h2: Mesa/Solis sit at the top of their page and own the h1,
 * and a page can only have one.
 */
export default function CarinaRendering({ content }: { content: GenericSectionContent }) {
  const { title, description, cards = [], cards_block_alignment, ctas, additional_css, additional_css_class_names } = content;
  const { uniqueClass, scopedAdditionalCss } = useAdditionalCss(additional_css, 'CarinaRendering');

  const card = cards[0];
  const imageLeft = cards_block_alignment !== 'Right';

  return (
    <>
      {scopedAdditionalCss && <style dangerouslySetInnerHTML={{ __html: scopedAdditionalCss }} />}
      <div className={`${SECTION_SPACING} px-0 lg:px-80 2xl:px-120 ${[additional_css_class_names, uniqueClass].filter(Boolean).join(' ')}`}>
        <div className={`flex items-center gap-24 lg:gap-0 ${imageLeft ? 'flex-col lg:flex-row' : 'flex-col lg:flex-row-reverse'}`}>
          <div className="relative w-full lg:flex-1 lg:flex-grow">
            {card?.desktop_image?.url && (
              //Served straight off the Contentstack image API rather than through the Next loader:
              //both <source> media queries cover the whole range, so the <img>'s own srcset is never
              //consulted. That is what caps the desktop file at 1024w and forces webp.
              <picture>
                <source media="(max-width:699px)" srcSet={`${card.desktop_image.url}?format=webp`} />
                <source media="(min-width:700px)" srcSet={`${card.desktop_image.url}?w=1024&format=webp 1024w`} />
                <Image
                  loading="eager"
                  src={card.desktop_image.url}
                  alt={card.desktop_image.description ?? title ?? ''}
                  sizes="100vw"
                  style={{ width: '100%', objectFit: 'cover' }}
                  width={500}
                  height={300}
                  className="lg:h-[50rem] xl:h-[55rem] 2xl:h-[45rem]"
                />
              </picture>
            )}
          </div>
          <div className="flex-1 flex-grow">
            <div className={`px-24 md:px-28 lg:px-48 xl:px-60 2xl:px-80 ${imageLeft ? 'md:pr-0 2xl:pr-0' : '2xl:pl-0'}`}>
              {title && (
                <Typography as="h2" className={`pb-20 text-32 font-medium leading-10 text-[#090909] xl:text-28 2xl:text-32 ${Tiempos.className}`}>
                  {title}
                </Typography>
              )}
              {description && (
                <div
                  className="flex flex-col gap-16 text-[1rem] font-normal leading-6 text-[#333] lg:text-[1.25rem] xl:text-14 2xl:text-[1rem]"
                  dangerouslySetInnerHTML={{ __html: sanitizeHtml(description) }}
                />
              )}
              {ctas && ctas.length > 0 && (
                <div className="mb-10 mt-22 flex flex-col gap-10 md:mt-48 md:flex-row">
                  {ctas.map((cta: Cta, index: number) => (
                    <Link
                      key={index}
                      link={cta?.url ?? '#'}
                      target={cta?.target || '_self'}
                      rel="noopener noreferrer"
                    >
                      <Button
                        className={
                          index === 0
                            ? 'border-1 w-full rounded-none border border-[#2B2C39] bg-white px-18 py-10 shadow-sm hover:!border-[#F4F4F4] hover:!bg-[#F4F4F4] md:w-fit md:px-32 md:py-18'
                            : 'border-1 w-full rounded-none border border-[#2B2C39] bg-[#2B2C39] px-18 py-10 shadow-sm hover:!bg-[#394A54] md:w-fit md:px-32 md:py-18'
                        }
                      >
                        <Typography
                          as="span"
                          className={`text-16 font-medium leading-6 xl:text-14 2xl:text-16 ${NationalSemibold.className} ${index === 0 ? 'text-[#090909]' : 'text-white'}`}
                        >
                          {cta?.label}
                        </Typography>
                      </Button>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
